/**
 * 高性能音频队列管理器
 * 专门为处理大量音频数据和紧急插播优化
 */

// 最小堆实现（用于紧急队列按时间排序）
class MinHeap<T> {
  private heap: T[] = [];
  private compare: (a: T, b: T) => number;

  constructor(compareFunction: (a: T, b: T) => number) {
    this.compare = compareFunction;
  }

  push(element: T): void {
    this.heap.push(element);
    this.heapifyUp(this.heap.length - 1);
  }

  pop(): T | null {
    if (this.heap.length === 0) return null;

    const result = this.heap[0];
    const end = this.heap.pop()!;

    if (this.heap.length > 0) {
      this.heap[0] = end;
      this.heapifyDown(0);
    }

    return result;
  }

  peek(): T | null {
    return this.heap.length > 0 ? this.heap[0] : null;
  }

  private heapifyUp(index: number): void {
    while (index > 0) {
      const parentIndex = Math.floor((index - 1) / 2);
      if (this.compare(this.heap[index], this.heap[parentIndex]) >= 0) break;

      [this.heap[index], this.heap[parentIndex]] = [
        this.heap[parentIndex],
        this.heap[index]
      ];
      index = parentIndex;
    }
  }

  private heapifyDown(index: number): void {
    while (true) {
      let minIndex = index;
      const leftChild = 2 * index + 1;
      const rightChild = 2 * index + 2;

      if (
        leftChild < this.heap.length &&
        this.compare(this.heap[leftChild], this.heap[minIndex]) < 0
      ) {
        minIndex = leftChild;
      }

      if (
        rightChild < this.heap.length &&
        this.compare(this.heap[rightChild], this.heap[minIndex]) < 0
      ) {
        minIndex = rightChild;
      }

      if (minIndex === index) break;

      [this.heap[index], this.heap[minIndex]] = [
        this.heap[minIndex],
        this.heap[index]
      ];
      index = minIndex;
    }
  }

  get size(): number {
    return this.heap.length;
  }

  clear(): void {
    this.heap = [];
  }

  // 🔧 新增：获取所有数据（用于兼容现有功能）
  toArray(): T[] {
    return [...this.heap].sort(this.compare);
  }
}

// 环形缓冲区（用于普通队列）
class CircularBuffer<T> {
  private buffer: (T | undefined)[];
  private capacity: number;
  private head = 0;
  private tail = 0;
  private size = 0;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.buffer = new Array(capacity);
  }

  enqueue(item: T): void {
    if (this.size === this.capacity) {
      // 满了就覆盖最老的数据
      this.head = (this.head + 1) % this.capacity;
    } else {
      this.size++;
    }

    this.buffer[this.tail] = item;
    this.tail = (this.tail + 1) % this.capacity;
  }

  dequeue(): T | null {
    if (this.size === 0) return null;

    const item = this.buffer[this.head];
    this.buffer[this.head] = undefined; // 清理引用
    this.head = (this.head + 1) % this.capacity;
    this.size--;

    return item || null;
  }

  peek(): T | null {
    return this.size > 0 ? this.buffer[this.head] || null : null;
  }

  get length(): number {
    return this.size;
  }

  clear(): void {
    this.buffer.fill(undefined);
    this.head = 0;
    this.tail = 0;
    this.size = 0;
  }

  // 🔧 新增：获取所有数据（用于兼容现有功能）
  toArray(): T[] {
    const result: T[] = [];
    let current = this.head;
    for (let i = 0; i < this.size; i++) {
      const item = this.buffer[current];
      if (item !== undefined) {
        result.push(item);
      }
      current = (current + 1) % this.capacity;
    }
    return result;
  }

  // 🔧 新增：按索引访问（用于兼容现有功能）
  get(index: number): T | null {
    if (index < 0 || index >= this.size) return null;
    const actualIndex = (this.head + index) % this.capacity;
    return this.buffer[actualIndex] || null;
  }
}

// 音频队列项目接口
export interface AudioQueueItem {
  id: string;
  text: string;
  audioUrl: string;
  status: "pending" | "playing" | "completed" | "error";
  commentId?: string | null;
  voiceNo?: number; // -1 表示紧急插播
  createTime?: number; // 时间戳
  playedCharCount?: number;
  // 保持其他现有字段的兼容性
  [key: string]: any;
}

// 高性能音频队列管理器
export class HighPerformanceAudioQueue {
  // 紧急队列：使用最小堆按时间排序
  private urgentHeap: MinHeap<AudioQueueItem>;

  // 普通队列：使用环形缓冲区
  private normalBuffer: CircularBuffer<AudioQueueItem>;

  // 已移除批处理机制，直接处理

  // 统计信息
  private stats = {
    urgentCount: 0,
    normalCount: 0,
    totalProcessed: 0
  };

  constructor(normalCapacity = 10000) {
    // 紧急队列按createTime升序排序
    this.urgentHeap = new MinHeap<AudioQueueItem>(
      (a, b) => (a.createTime || 0) - (b.createTime || 0)
    );

    // 普通队列使用环形缓冲区
    this.normalBuffer = new CircularBuffer<AudioQueueItem>(normalCapacity);

    // 队列初始化完成
  }

  /**
   * 添加音频项目到队列
   */
  addItem(item: AudioQueueItem): void {
    // 确保基本字段
    const processedItem: AudioQueueItem = {
      id: item.id || String(Date.now() + Math.random()),
      text: item.text || "",
      audioUrl: item.audioUrl || "",
      status: item.status || "pending",
      commentId: item.commentId || null,
      voiceNo: item.voiceNo,
      createTime: item.createTime || Date.now(),
      playedCharCount: item.playedCharCount || 0,
      ...item // 保留其他字段
    };

    if (processedItem.voiceNo === -1) {
      // 紧急插播：直接加入最小堆
      this.urgentHeap.push(processedItem);
      this.stats.urgentCount++;
    } else {
      // 普通消息：直接加入普通队列
      this.normalBuffer.enqueue(processedItem);
      this.stats.normalCount++;
    }

    this.stats.totalProcessed++;
  }

  /**
   * 获取下一个要播放的音频项目
   */
  getNext(): AudioQueueItem | null {
    // 优先处理紧急队列
    if (this.urgentHeap.size > 0) {
      const item = this.urgentHeap.pop();
      if (item) {
        return item;
      }
    }

    // 处理普通队列
    const item = this.normalBuffer.dequeue();
    return item;
  }

  // 批处理相关方法已移除，直接入队

  /**
   * 兼容性方法：获取队列长度（模拟原始数组行为）
   */
  get length(): number {
    return this.urgentHeap.size + this.normalBuffer.length;
  }

  /**
   * 兼容性方法：获取所有队列数据（按优先级排序）
   */
  toArray(): AudioQueueItem[] {
    // 合并紧急队列和普通队列
    const urgentItems = this.urgentHeap.toArray();
    const normalItems = this.normalBuffer.toArray();

    return [...urgentItems, ...normalItems];
  }

  /**
   * 🔧 兼容性方法：按索引访问（优先级排序后的索引）
   */
  get(index: number): AudioQueueItem | null {
    const allItems = this.toArray();
    return allItems[index] || null;
  }

  /**
   * 兼容性方法：清空队列
   */
  clear(): void {
    this.urgentHeap.clear();
    this.normalBuffer.clear();

    this.stats = {
      urgentCount: 0,
      normalCount: 0,
      totalProcessed: 0
    };

    // 队列已清空
  }

  /**
   * 获取队列统计信息
   */
  getStats() {
    return {
      ...this.stats,
      urgentPending: this.urgentHeap.size,
      normalPending: this.normalBuffer.length,
      batchPending: 0,
      totalPending: this.length
    };
  }

  /**
   * 🔧 兼容性方法：支持原始数组的push操作
   */
  push(item: AudioQueueItem): number {
    this.addItem(item);
    return this.length;
  }

  /**
   * 🔧 兼容性方法：支持原始数组的shift操作
   */
  shift(): AudioQueueItem | undefined {
    return this.getNext() || undefined;
  }

  /**
   * 检查是否有紧急插播待处理
   */
  hasUrgentItems(): boolean {
    return this.urgentHeap.size > 0;
  }

  /**
   * 获取紧急队列数据（用于UI同步）
   */
  getUrgentItems(): AudioQueueItem[] {
    return this.urgentHeap.toArray();
  }

  /**
   * 获取普通队列数据（用于UI同步）
   */
  getNormalItems(): AudioQueueItem[] {
    return this.normalBuffer.toArray();
  }
}
